import React, {useCallback, useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {format, isBefore} from 'date-fns';
import {useTranslation} from 'react-i18next';

interface TimeFilterProps {
  startTime: string | null;
  endTime: string | null;
  onChangeTime: (field: 'startTime' | 'endTime', time: string) => void;
  onErrorChange: (error: boolean) => void;
}

const TimeFilter: React.FC<TimeFilterProps> = ({
  startTime,
  endTime,
  onChangeTime,
  onErrorChange
}) => {
  const {t} = useTranslation();

  const [error, setError] = useState<string | null>(null);

  const handleStartTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        const formattedTime = format(time, 'HH:mm:ss');
        onChangeTime('startTime', formattedTime);
      }
    },
    [onChangeTime]
  );

  const handleEndTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        const formattedTime = format(time, 'HH:mm:ss');
        onChangeTime('endTime', formattedTime);
      }
    },
    [onChangeTime]
  );

  useEffect(() => {
    if (startTime && endTime) {
      const startDateTime = new Date(`1970-01-01T${startTime}`);
      const endDateTime = new Date(`1970-01-01T${endTime}`);
      if (isBefore(endDateTime, startDateTime)) {
        setError(t('validation:time_in_past'));
        onErrorChange(true);
      } else {
        setError(null);
        onErrorChange(false);
      }
    } else {
      setError(null);
      onErrorChange(false);
    }
  }, [startTime, endTime, t, onErrorChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        width: '100%',
        justifyContent: 'center'
      }}
    >
      <TimePicker
        label={t('start_time')}
        value={startTime ? new Date(`1970-01-01T${startTime}`) : null}
        onChange={handleStartTimeChange}
        sx={{
          width: '50%'
        }}
      />

      <TimePicker
        label={t('end_time')}
        value={endTime ? new Date(`1970-01-01T${endTime}`) : null}
        onChange={handleEndTimeChange}
        slotProps={{
          textField: {
            error: !!error,
            helperText: error || ''
          }
        }}
        sx={{
          width: '50%'
        }}
      />
    </Box>
  );
};

export default TimeFilter;
